import type { IssuedPass, PassStatus } from '@/api/passes'
import { t } from '@/i18n'
import { dateWords } from './pass-describe'

/// Sold passes in the panel: the filter, the one-line state, what each
/// action may do now. The SERVER keeps the state machine
/// (specs/issued-passes.md §2); this only decides which buttons make sense
/// for the status the server reported (`status` is the effective one).

export const FILTERS = ['all', 'active', 'pending', 'frozen', 'expired', 'revoked'] as const
export type PassFilter = (typeof FILTERS)[number]

export const filterPasses = (passes: readonly IssuedPass[], filter: PassFilter): readonly IssuedPass[] =>
  filter === 'all' ? passes : passes.filter((pass) => pass.status === filter)

export const countByFilter = (passes: readonly IssuedPass[]): Readonly<Record<PassFilter, number>> =>
  Object.fromEntries(FILTERS.map((filter) => [filter, filterPasses(passes, filter).length])) as Record<
    PassFilter,
    number
  >

/// What the desk may do to a pass in this state — the transitions of §2.
export const allowedActions = (status: PassStatus) => ({
  freeze: status === 'active',
  unfreeze: status === 'frozen',
  extend: status !== 'revoked',
  transfer: status === 'active' || status === 'frozen' || status === 'pending',
  revoke: status === 'active' || status === 'frozen' || status === 'pending',
})

/// «до 31 жовт.», «з 3 лист.», «стартує з першого візиту» — how long the
/// pass runs, in words. `null` when it has no dates at all. The «Строк»
/// column of «Продані», and the first half of `passLine`.
export const passDates = (pass: IssuedPass, today = new Date()): string | null => {
  const s = t.passes.sold
  return pass.startsOn
    ? pass.endsOn
      ? s.until(dateWords(pass.endsOn, today))
      : s.from(dateWords(pass.startsOn, today))
    : pass.startRule === 'on_first_visit'
      ? s.firstVisit
      : pass.endsOn
        ? s.until(dateWords(pass.endsOn, today))
        : null
}

/// «8 з 12», or each group pack («Басейн 5 з 8») — visits left. `null` when
/// nothing is counted (a безліміт): the column then says so in words.
export const passVisits = (pass: IssuedPass): string | null => {
  const s = t.passes.sold
  return pass.visitsTotal != null
    ? s.visitsOf(pass.visitsLeft ?? 0, pass.visitsTotal)
    : (pass.groups ?? [])
        .filter((g) => g.visitsTotal != null)
        .map((g) => `${g.label} ${s.visitsOf(g.visitsLeft ?? 0, g.visitsTotal ?? 0)}`)
        .join(', ') || null
}

/// «до 31 жовт. · 8 з 12» — dates and the counter, as the row's second line.
export const passLine = (pass: IssuedPass, today = new Date()): string =>
  [passDates(pass, today), passVisits(pass)].filter(Boolean).join(' · ')

/// Days of freezing still allowed, and freezes left — the hint in the form.
export const freezeRoom = (pass: IssuedPass): { days: number; count: number } | null =>
  pass.freezePolicy
    ? {
        days: Math.max(0, pass.freezePolicy.maxDays - pass.frozenDays),
        count: Math.max(0, pass.freezePolicy.maxCount - pass.freezeCount),
      }
    : null

export const addDaysIso = (iso: string, days: number): string => {
  const [year = 1970, month = 1, day = 1] = iso.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10)
}
